"use client";

import { EMPTY_ARRAY } from "@/lib/utils/stable-empty";
import { useCallback, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuthedFetch } from "@/lib/hooks/use-authed-fetch";
import {
  getRealmLabel,
  normalizeRealmId,
  setRealmLabelOverrides,
  QB_REALM_CATEGORY,
  type RealmLabelEntry,
  type QbRealmCategory,
} from "@/lib/integrations/quickbooks/realms";

/**
 * Client access to the runtime-editable QuickBooks realm labels.
 *
 * Reads the `/api/quickbooks/realm-labels` store (admin-editable) via TanStack
 * Query so every RealmBadge/filter shares ONE cached request. Falls back to the
 * hardcoded registry constant while loading or on error, so labels always render.
 */

const REALM_LABELS_QUERY_KEY = ["quickbooks-realm-labels"] as const;

interface RealmLabelsResponse {
  entries: RealmLabelEntry[];
}

export function useRealmLabels() {
  const authedFetch = useAuthedFetch();

  const query = useQuery({
    queryKey: REALM_LABELS_QUERY_KEY,
    queryFn: async (): Promise<RealmLabelEntry[]> => {
      const res = await authedFetch("/api/quickbooks/realm-labels");
      if (!res.ok) throw new Error(`Failed to load realm labels (${res.status})`);
      const json = (await res.json()) as RealmLabelsResponse;
      return json.entries ?? [];
    },
    staleTime: 5 * 60 * 1000,
  });

  const entries = query.data ?? EMPTY_ARRAY;

  // Prime the sync getRealmLabel override cache so client-side sync callers
  // (detail-header models, Slack-free UI helpers) reflect admin edits too.
  useEffect(() => {
    if (entries.length === 0) return;
    const overrides: Record<string, string> = {};
    for (const entry of entries) overrides[entry.realmId] = entry.label;
    setRealmLabelOverrides(overrides);
  }, [entries]);

  /** Resolve a realm id's entry from the store; null if not in the store. */
  const getEntry = useCallback(
    (realmId: string | null | undefined): RealmLabelEntry | null => {
      if (!realmId) return null;
      const norm = normalizeRealmId(realmId);
      return entries.find((e) => normalizeRealmId(e.realmId) === norm) ?? null;
    },
    [entries]
  );

  /** Store-first label, falling back to the constant registry. */
  const getLabel = useCallback(
    (realmId: string | null | undefined): string => {
      if (!realmId) return "";
      return getEntry(realmId)?.label ?? getRealmLabel(realmId);
    },
    [getEntry]
  );

  /** Store-first category, falling back to the constant registry. */
  const getCategory = useCallback(
    (realmId: string | null | undefined): QbRealmCategory => {
      if (!realmId) return "unknown";
      return getEntry(realmId)?.category ?? QB_REALM_CATEGORY[normalizeRealmId(realmId)] ?? "unknown";
    },
    [getEntry]
  );

  return {
    entries,
    isLoading: query.isLoading,
    error: query.error,
    getEntry,
    getLabel,
    getCategory,
  };
}

interface UpsertRealmLabelVars {
  realm_id: string;
  label: string;
  category?: QbRealmCategory;
  is_active?: boolean;
}

export function useUpsertRealmLabel() {
  const authedFetch = useAuthedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (vars: UpsertRealmLabelVars): Promise<RealmLabelEntry> => {
      const res = await authedFetch("/api/quickbooks/realm-labels", {
        method: "POST",
        body: JSON.stringify(vars),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error ?? "Failed to save realm label");
      return json.entry as RealmLabelEntry;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: REALM_LABELS_QUERY_KEY });
    },
  });
}

export function useDeleteRealmLabel() {
  const authedFetch = useAuthedFetch();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (realmId: string): Promise<void> => {
      const res = await authedFetch(
        `/api/quickbooks/realm-labels?realm_id=${encodeURIComponent(realmId)}`,
        { method: "DELETE" }
      );
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        throw new Error(json?.error ?? "Failed to delete realm label");
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: REALM_LABELS_QUERY_KEY });
    },
  });
}
